import { ErrorBoundary } from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createHead, UnheadProvider } from '@unhead/react/client';
import { enGB } from 'date-fns/locale/en-GB';
import { it } from 'date-fns/locale/it';
import { setDefaultOptions } from 'date-fns/setDefaultOptions';
import { useTranslation as useI18nTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import * as z from 'zod';
import { queryClient } from './client/queryClient';
import { ErrorBoundaryFallback } from './components/layout/ErrorBoundaryFallback';
import { ChatProvider } from './hooks/Chat/IntercomChat';
import { useErrorMap } from './hooks/forms/useErrorMap';
import { useAuthSync } from './hooks/useAuthSync';
import { decorateScopeContexts } from './hooks/useExceptionLogger';
import router from './routes/routes';
import { SibillSocketProvider } from './state/notifications/socket';
import ThemeConfig from './theme';

export default function App() {
  const { i18n } = useI18nTranslation();
  setDefaultOptions({ locale: i18n.language === 'it' ? it : enGB });
  const errorMap = useErrorMap();
  z.setErrorMap(errorMap);
  useAuthSync();
  const head = createHead();

  return (
    <ErrorBoundary
      beforeCapture={(scope, error) => {
        decorateScopeContexts(scope, error);
      }}
      fallback={ErrorBoundaryFallback}
    >
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} position="bottom" buttonPosition="top-left" />
        <UnheadProvider head={head}>
          <ThemeConfig>
            <SibillSocketProvider>
              <ChatProvider>
                <RouterProvider router={router} />
              </ChatProvider>
            </SibillSocketProvider>
          </ThemeConfig>
        </UnheadProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
